import React, { useEffect, useState } from "react";
import { FaEnvelope, FaFolderOpen, FaPhone, FaSlidersH, FaUser, FaWhatsapp, FaWhatsappSquare } from "react-icons/fa";
import { Badge } from "./common";
import { SetupPctPill } from "./setup/setupUi";
import { errorMessage, fmtNum } from "../dmApi";
import { ResultsMobileCard, ResultsMobileList } from "../../../components/ResultsMobile";
import useCompactViewport from "../../../hooks/useCompactViewport";
import TablePagination, {
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
  readStoredRowsPerPage,
} from "../../../components/TablePagination";
import "../../../assets/css/DigixLeads.css";

const ROWS_STORAGE_KEY = "dmClientsRowsPerPage";

const dash = <span className="dmp-muted">—</span>;

const digitsOnly = (value) => String(value || "").replace(/\D/g, "");

function telHref(phone) {
  const digits = digitsOnly(phone);
  if (!digits) return null;
  return `tel:${String(phone).trim().startsWith("+") ? "+" : ""}${digits}`;
}

// WhatsApp needs the international number, so only phones stored with a country code qualify.
function whatsAppHref(phone) {
  if (!String(phone || "").trim().startsWith("+")) return null;
  const digits = digitsOnly(phone);
  return digits ? `https://wa.me/${digits}` : null;
}

const stop = (e) => e.stopPropagation();

const SCOPE_LABEL = { P: "Personal", C: "Company" };

const CONTACT_KINDS = {
  call: { Icon: FaPhone, verb: "Call", flip: true, shrink: 2 },
  email: { Icon: FaEnvelope, verb: "Email", shrink: 2 },
  whatsapp: { Icon: FaWhatsapp, verb: "WhatsApp" },
  waBusiness: { Icon: FaWhatsappSquare, verb: "WhatsApp Business" },
};

function contactHref(kind, value) {
  if (kind === "call") return telHref(value);
  if (kind === "email") return String(value || "").trim() ? `mailto:${String(value).trim()}` : null;
  return whatsAppHref(value);
}

function ContactIcon({ kind, scope, value, iconSize, unavailableTitle }) {
  const { Icon, verb, flip, shrink = 0 } = CONTACT_KINDS[kind];
  const href = contactHref(kind, value);
  const scopeName = SCOPE_LABEL[scope];
  const opensNewTab = kind === "whatsapp" || kind === "waBusiness";
  const className = `dmp-contact-action dmp-contact-action--${kind} dmp-contact-action--scope-${scope}`;
  const content = (
    <>
      <Icon size={iconSize - shrink} style={flip ? { transform: "scaleX(-1)" } : undefined} />
      <span className="dmp-contact-action__scope" aria-hidden="true">
        {scope}
      </span>
    </>
  );
  if (!href) {
    return (
      <span
        className={`${className} is-disabled`}
        title={unavailableTitle || `${scopeName} ${verb.toLowerCase()} unavailable`}
        aria-disabled="true"
      >
        {content}
      </span>
    );
  }
  return (
    <a
      className={className}
      href={href}
      target={opensNewTab ? "_blank" : undefined}
      rel={opensNewTab ? "noopener noreferrer" : undefined}
      title={`${verb} (${scopeName.toLowerCase()}) ${value}`}
      aria-label={`${verb} ${scopeName.toLowerCase()}`}
      onClick={stop}
    >
      {content}
    </a>
  );
}

function ClientContactActions({
  phone,
  email,
  personalPhone,
  personalEmail,
  size = "md",
  projectCount,
  onViewProjects,
}) {
  const iconSize = size === "sm" ? 12 : 14;
  const count = Number(projectCount) || 0;
  return (
    <div className="dmp-contact-actions">
      {onViewProjects ? (
        <button
          type="button"
          className="dmp-contact-action dmp-contact-action--projects"
          title={`View projects (${count})`}
          aria-label={`View projects (${count})`}
          onClick={(e) => {
            e.stopPropagation();
            onViewProjects();
          }}
        >
          <FaFolderOpen size={iconSize} />
          {count > 0 ? <span className="dmp-contact-action__badge">{count}</span> : null}
        </button>
      ) : null}
      <ContactIcon
        kind="whatsapp"
        scope="P"
        value={personalPhone}
        iconSize={iconSize}
        unavailableTitle="Personal WhatsApp unavailable (needs a phone with country code)"
      />
      <ContactIcon kind="email" scope="P" value={personalEmail} iconSize={iconSize} />
      <span className="dmp-contact-actions__divider" aria-hidden="true" />
      <ContactIcon kind="call" scope="C" value={phone} iconSize={iconSize} />
      <ContactIcon kind="email" scope="C" value={email} iconSize={iconSize} />
      <ContactIcon
        kind="waBusiness"
        scope="C"
        value={phone}
        iconSize={iconSize}
        unavailableTitle="Company WhatsApp Business unavailable (company phone needs a country code)"
      />
    </div>
  );
}

/**
 * Staff DM client search: name, business type and include-inactive filters over every
 * DM-enrolled client, with results in a table. Clicking a row opens the client.
 * ``actions`` render at the right end of the filter row.
 */
function ClientSearch({ api, onSelect, onViewProjects, actions = null }) {
  const [name, setName] = useState("");
  const [businessTypeId, setBusinessTypeId] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => readStoredRowsPerPage(ROWS_STORAGE_KEY));
  const isCompactViewport = useCompactViewport();
  const term = name.trim();

  useEffect(() => {
    api
      .businessTypes()
      .then((list) => setBusinessTypes(list || []))
      .catch(() => setBusinessTypes([]));
  }, [api]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      setError("");
      try {
        const res = await api.tenants(term, {
          all: true,
          businessTypeId: businessTypeId || null,
          includeInactive,
        });
        if (!cancelled) setRows(res?.enrolled || []);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Could not load clients"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, term ? 300 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [api, term, businessTypeId, includeInactive]);

  useEffect(() => {
    setCurrentPage(1);
  }, [term, businessTypeId, includeInactive]);

  const hasFilters = Boolean(term || businessTypeId || includeInactive);
  const totalPages = Math.max(1, Math.ceil(rows.length / rowsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const pageRows = rows.slice((safePage - 1) * rowsPerPage, safePage * rowsPerPage);

  const handleRowsPerPageChange = (next) => {
    setRowsPerPage(next);
    setCurrentPage(1);
    try {
      localStorage.setItem(ROWS_STORAGE_KEY, String(next));
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="dmp-client-search">
      {isCompactViewport ? (
        <div className={`dmp-client-search__compact co-filters-collapsible${filtersOpen ? " is-open" : ""}`}>
          <div className="co-filter-search-bar">
            <input
              type="text"
              className="co-filter-search-bar__input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Search company, contact, phone or email"
              aria-label="Search clients"
              autoComplete="off"
            />
            <button
              type="button"
              className={`co-filter-search-bar__icon${filtersOpen ? " is-active" : ""}`}
              onClick={() => setFiltersOpen((o) => !o)}
              aria-expanded={filtersOpen}
              aria-label={filtersOpen ? "Hide Filters" : "Show Filters"}
              title={filtersOpen ? "Hide Filters" : "Show Filters"}
            >
              <FaSlidersH aria-hidden />
            </button>
          </div>
        </div>
      ) : null}

      <div
        className={`dmp-filters${isCompactViewport ? " dmp-client-search__filters--compact" : ""}`}
        hidden={isCompactViewport && !filtersOpen}
      >
        {!isCompactViewport ? (
          <label className="dmp-field dmp-client-search__name">
            Name
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Company, contact person, phone or email"
              autoComplete="off"
            />
          </label>
        ) : null}
        <label className="dmp-field dmp-client-search__type">
          Business Type
          <select value={businessTypeId} onChange={(e) => setBusinessTypeId(e.target.value)}>
            <option value="">All business types</option>
            {businessTypes.map((b) => (
              <option key={b.Domain_Id} value={b.Domain_Id}>
                {b.Domain_Name}
              </option>
            ))}
          </select>
        </label>
        <label className="dmp-field dmp-client-search__check">
          In-Active
          <input
            type="checkbox"
            className="dmp-client-search__check-box"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
        </label>
        {hasFilters ? (
          <button
            type="button"
            className="dmp-btn dmp-btn--ghost"
            onClick={() => {
              setName("");
              setBusinessTypeId("");
              setIncludeInactive(false);
            }}
          >
            Clear
          </button>
        ) : null}
        {actions && !isCompactViewport ? <div className="dmp-client-search__actions">{actions}</div> : null}
      </div>
      {actions && isCompactViewport ? <div className="dmp-client-search__actions">{actions}</div> : null}

      {error ? <div className="dmp-error">{error}</div> : null}

      <ResultsMobileList
        loading={loading && rows.length === 0}
        loadingLabel="Loading clients…"
        emptyLabel={hasFilters ? "No clients match these filters." : "No clients are enrolled in Digital Marketing yet."}
      >
        {pageRows.map((t) => {
          const contact = t.Person_Name && t.Person_Name !== t.Name ? t.Person_Name : null;
          const services = t.Dm_Access?.services || [];
          const isActive = t.Status === "Active";
          const personalPhone = t.Personal_Phone;
          const personalEmail = t.Personal_Email;
          return (
            <ResultsMobileCard
              key={t.tenant_id}
              title={t.Name}
              meta={[
                t.Status ? { label: t.Status, tone: isActive ? "success" : "danger" } : null,
                t.Business_Type
                  ? { label: t.Business_Model ? `${t.Business_Type} · ${t.Business_Model}` : t.Business_Type, tone: "info" }
                  : null,
                t.Setup_Pct != null ? { label: `Setup ${t.Setup_Pct}%`, tone: t.Setup_Pct === 100 ? "success" : "warning" } : null,
              ].filter(Boolean)}
              onOpen={() => onSelect(t)}
              actions={
                <div className="dmp-client-card__actions">
                  <ClientContactActions
                    phone={t.Company_Phone}
                    email={t.Company_Email}
                    personalPhone={personalPhone}
                    personalEmail={personalEmail}
                    projectCount={t.Project_Count}
                    onViewProjects={onViewProjects ? () => onViewProjects(t) : undefined}
                  />
                </div>
              }
            >
              {contact || personalPhone ? (
                <ul className="co-tcard__contact">
                  {contact ? (
                    <li>
                      <FaUser aria-hidden="true" />
                      <span>{contact}</span>
                    </li>
                  ) : null}
                  {personalPhone ? (
                    <li>
                      <FaPhone aria-hidden="true" style={{ transform: "scaleX(-1)" }} />
                      <a className="dmp-contact-link" href={telHref(personalPhone)} onClick={stop}>
                        {personalPhone}
                      </a>
                    </li>
                  ) : null}
                </ul>
              ) : null}
              <dl className="co-tcard__info">
                <div className="co-tcard__info-full">
                  <dt>DM Services</dt>
                  <dd>
                    {services.length ? (
                      <span className="dmp-chips">
                        {services.map((s) => (
                          <Badge key={s} value="Organic">
                            {s}
                          </Badge>
                        ))}
                      </span>
                    ) : (
                      "—"
                    )}
                  </dd>
                </div>
              </dl>
            </ResultsMobileCard>
          );
        })}
      </ResultsMobileList>

      <div className="dmp-table-wrap co-desktop-table">
        <table className="dmp-table">
          <thead>
            <tr>
              <th>Company Name</th>
              <th>Business Type</th>
              <th>Contact Person</th>
              <th>Phone</th>
              <th>DM Services</th>
              <th className="dmp-num">Projects</th>
              <th>Setup</th>
              <th>Status</th>
              <th className="dmp-client-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="dmp-empty">
                  Loading clients…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="dmp-empty">
                  {hasFilters ? "No clients match these filters." : "No clients are enrolled in Digital Marketing yet."}
                </td>
              </tr>
            ) : (
              pageRows.map((t) => (
                <tr key={t.tenant_id} className="is-clickable" onClick={() => onSelect(t)}>
                  <td>
                    <button
                      type="button"
                      className="dmp-name-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(t);
                      }}
                    >
                      {t.Name}
                    </button>
                  </td>
                  <td>
                    {t.Business_Type || dash}
                    {t.Business_Model ? <div className="dmp-muted">{t.Business_Model} based</div> : null}
                  </td>
                  <td>{t.Person_Name && t.Person_Name !== t.Name ? t.Person_Name : dash}</td>
                  <td>
                    {t.Personal_Phone ? (
                      <a
                        className="dmp-contact-link"
                        href={telHref(t.Personal_Phone)}
                        onClick={stop}
                        title={`Call (personal) ${t.Personal_Phone}`}
                      >
                        {t.Personal_Phone}
                      </a>
                    ) : (
                      dash
                    )}
                  </td>
                  <td>
                    <span className="dmp-chips">
                      {(t.Dm_Access?.services || []).map((s) => (
                        <Badge key={s} value="Organic">
                          {s}
                        </Badge>
                      ))}
                    </span>
                  </td>
                  <td className="dmp-num">{fmtNum(t.Project_Count)}</td>
                  <td>
                    <SetupPctPill pct={t.Setup_Pct} />
                  </td>
                  <td>
                    <Badge value={t.Status === "Active" ? "Active" : "Inactive"}>{t.Status || "—"}</Badge>
                  </td>
                  <td className="dmp-client-actions-col">
                    <ClientContactActions
                      phone={t.Company_Phone}
                      email={t.Company_Email}
                      personalPhone={t.Personal_Phone}
                      personalEmail={t.Personal_Email}
                      size="sm"
                      projectCount={t.Project_Count}
                      onViewProjects={onViewProjects ? () => onViewProjects(t) : undefined}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!loading && rows.length > 0 ? (
        <TablePagination
          currentPage={safePage}
          totalItems={rows.length}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          itemLabel="clients"
          rowsPerPageSelectId="dm-clients-rows-per-page"
        />
      ) : null}
    </div>
  );
}

export default ClientSearch;
